import { createColumn } from "@repo/mfe-core";
import "./MetaDataTab.css";

import {
  Box,
  Button,
  Icon,
  InputField,
  TableSortable,
  Tooltip,
  Typography,
  useTooltipText,
} from "@saviynt/design-system";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { jsonToCSV } from "../util";
import CopyButton from "../../../../components/CopyButton/CopyButton";

const MetadataRowValue = ({ value }: { value: unknown }) => {
  const intl = useIntl();
  const [isHovered, setIsHovered] = useState(false);
  const isNoValue = value === "-";

  const isJsonOrArray = (val: unknown) => {
    if (val === "-") return false;
    return typeof val === "object" && val !== null;
  };

  const shouldWrapInPre = isJsonOrArray(value);

  return (
    <div
      className="flex justify-start items-center gap-2 truncate-text"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {shouldWrapInPre ? (
        <pre>
          <Typography kind="body2" color="--color-foreground-secondary-bold">
            {JSON.stringify(value, null, 2)}
          </Typography>
        </pre>
      ) : (
        <Typography kind="body2" color="--color-foreground-secondary-bold">
          {String(value)}
        </Typography>
      )}
      {!isNoValue && (
        <div style={{ visibility: isHovered ? "visible" : "hidden" }}>
          <CopyButton
            text={
              shouldWrapInPre ? JSON.stringify(value, null, 2) : String(value)
            }
            ariaLabel={intl.formatMessage(
              {
                defaultMessage: "Copy {value} to clipboard",
                description: "Aria label for copy button",
                id: "o8m+Eg",
              },
              {
                value: shouldWrapInPre
                  ? JSON.stringify(value, null, 2)
                  : String(value),
              },
            )}
          />
        </div>
      )}
    </div>
  );
};

const tableColumns = [
  createColumn("attribute", "Attribute", "8rem", { isSortable: false }),
  createColumn("value", "Value", "10rem", { isSortable: false }),
];

export default function MetaDataTab({
  data,
}: {
  data: Record<string, unknown>;
}) {
  const intl = useIntl();
  const [searchValue, setSearchValue] = useState("");
  const [columns, setColumns] = useState(tableColumns);
  const {
    tooltipText,
    handleTooltipTriggerClick,
    handleTooltipClose,
    tooltipCloseDelayAfterClick,
  } = useTooltipText({
    initialText: intl.formatMessage({
      defaultMessage: "Copy",
      id: "Eo0Htz",
      description: "Tooltip text for copy button in metadata tab",
    }),
    updatedText: intl.formatMessage({
      defaultMessage: "Copied",
      id: "dTovFT",
      description: "Tooltip text after clicking copy button in metadata tab",
    }),
    closeDelayAfterClick: 3000,
  });

  const transformedData = useMemo(
    () =>
      Object.entries(data).map(([key, val]) => ({
        attribute: key,
        value:
          val === null || (Array.isArray(val) && val.length === 0) ? "-" : val,
      })),
    [data],
  );

  const filteredData = useMemo(() => {
    const search = searchValue.toLowerCase();
    return transformedData.filter((row) => {
      if (row.attribute.toLowerCase().includes(search)) return true;
      const valueStr =
        typeof row.value === "object" && row.value !== null
          ? JSON.stringify(row.value)
          : String(row.value);
      return valueStr.toLowerCase().includes(search);
    });
  }, [searchValue, transformedData]);

  const handleCopyToClipboard = useCallback(async (): Promise<void> => {
    if (!filteredData) {
      console.warn("No data to copy to clipboard");

      return;
    }

    try {
      const csv = jsonToCSV(filteredData);
      await navigator.clipboard.writeText(csv);
    } catch (error) {
      console.error("Failed to copy text to clipboard:", error);
    }
  }, [filteredData]);

  const handleCopy = useCallback(async (): Promise<void> => {
    handleTooltipTriggerClick();
    await handleCopyToClipboard();
  }, [handleTooltipTriggerClick, handleCopyToClipboard]);

  const isCopied = tooltipText === "Copied";
  const tableRows = useMemo(
    () =>
      filteredData.map((row) => ({
        id: row.attribute,
        attribute: (
          <Typography kind="body2" color="--color-foreground-secondary-bold">
            {row.attribute}
          </Typography>
        ),
        value: <MetadataRowValue value={row.value} />,
      })),
    [filteredData],
  );

  return (
    <Box className="nhi-metadata-tab-container">
      <div className="flex justify-center items-center gap-4 p-4">
        <InputField
          name="basic"
          placeholder="Search"
          kind="search"
          size="medium"
          prefixIcon={
            <Icon kind="Search" color="--color-foreground-neutral-subtle" />
          }
          value={searchValue}
          setValue={setSearchValue}
        />
        <Tooltip
          text={tooltipText}
          placement="top"
          closeDelayAfterClick={tooltipCloseDelayAfterClick}
          onClose={handleTooltipClose}
          isDisabled={isCopied}
          trigger={
            <Button
              type="button"
              kind="ghost"
              size="medium"
              isSuccess={isCopied}
              leftIcon={
                <Icon
                  kind={isCopied ? "Check" : "Copy"}
                  size="medium"
                  color="neutral-100"
                />
              }
              onClick={handleCopy}
              dataTestId="copy-button"
            />
          }
        />
      </div>
      <Box>
        <TableSortable
          columnConfigs={{ isDndDisabled: true }}
          columns={columns}
          setColumns={setColumns}
          data={tableRows}
          isVirtualizationEnabled={false}
        />
      </Box>
    </Box>
  );
}
